import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { KeyRound, Mail, Lock, Loader2, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import PasskeyPrompt from "@/components/PasskeyPrompt";

interface LostPasskeyFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "login" | "mfa" | "passkey";

const LostPasskeyFlow = ({ open, onOpenChange }: LostPasskeyFlowProps) => {
  const { signIn, user } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [factorId, setFactorId] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep("login");
      setEmail("");
      setPassword("");
      setMfaCode("");
      setFactorId("");
    }
  }, [open]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast({ title: "فشل تسجيل الدخول", description: "بريد إلكتروني أو كلمة مرور غير صحيحة", variant: "destructive" });
        return;
      }
      // Load MFA factor
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = factors?.totp?.find((f) => f.status === "verified");
      if (verifiedFactor) {
        setFactorId(verifiedFactor.id);
        setStep("mfa");
      } else {
        toast({ title: "خطأ", description: "لا يوجد مصادقة ثنائية مفعلة على هذا الحساب", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (mfaCode.length !== 6 || !factorId) return;
    setMfaLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: mfaCode,
      });
      if (verifyError) throw verifyError;

      // MFA verified - delete old passkey credentials
      if (user) {
        await supabase.from("webauthn_credentials").delete().eq("user_id", user.id);
      }

      // Set persistent flags
      localStorage.setItem("mfa_verified", JSON.stringify({ userId: user?.id || "", timestamp: Date.now() }));
      localStorage.removeItem("mfa_required");

      toast({ title: "تم التحقق بنجاح ✅", description: "يمكنك الآن إنشاء مفتاح Passkey جديد" });
      setStep("passkey");
    } catch (err: any) {
      toast({ title: "رمز غير صحيح", description: "تحقق من رمز المصادقة الثنائية وحاول مرة أخرى", variant: "destructive" });
      setMfaCode("");
    } finally {
      setMfaLoading(false);
    }
  };

  if (step === "passkey") {
    return (
      <PasskeyPrompt
        open={true}
        onComplete={() => {
          onOpenChange(false);
          setStep("login");
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
              {step === "mfa" ? (
                <ShieldCheck className="w-8 h-8 text-primary" />
              ) : (
                <KeyRound className="w-8 h-8 text-primary" />
              )}
            </div>
          </div>
          <DialogTitle className="text-xl">
            {step === "mfa" ? "التحقق من المصادقة الثنائية" : "استعادة مفتاح Passkey"}
          </DialogTitle>
          <DialogDescription className="text-center pt-1">
            {step === "mfa"
              ? "أدخل رمز المصادقة الثنائية من تطبيق Authenticator"
              : "سجل دخولك بالبريد وكلمة المرور لإنشاء مفتاح Passkey جديد"}
          </DialogDescription>
        </DialogHeader>

        {step === "login" && (
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-foreground">البريد الإلكتروني</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="your@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-input border-border text-foreground pr-10 text-left"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-input border-border text-foreground pr-10"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading || !email || !password} className="w-full h-12 gradient-primary text-primary-foreground font-semibold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  التالي
                  <ArrowRight className="w-5 h-5 mr-2" />
                </>
              )}
            </Button>
          </form>
        )}

        {step === "mfa" && (
          <div className="flex flex-col items-center space-y-6 mt-4">
            <InputOTP
              maxLength={6}
              value={mfaCode}
              onChange={setMfaCode}
              disabled={mfaLoading}
            >
              <InputOTPGroup className="gap-2" dir="ltr">
                <InputOTPSlot index={0} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                <InputOTPSlot index={1} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                <InputOTPSlot index={2} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                <InputOTPSlot index={3} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                <InputOTPSlot index={4} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                <InputOTPSlot index={5} className="w-12 h-14 text-xl font-bold border-border bg-input" />
              </InputOTPGroup>
            </InputOTP>

            <Button
              onClick={handleMfaVerify}
              disabled={mfaCode.length !== 6 || mfaLoading}
              className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg"
            >
              {mfaLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  تأكيد وإنشاء مفتاح جديد
                  <ArrowRight className="w-5 h-5 mr-2" />
                </>
              )}
            </Button>

            <Button variant="link" onClick={() => setStep("login")} className="text-muted-foreground">
              العودة
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LostPasskeyFlow;
